import * as Sentry from "@sentry/react";

import {
  useCoveragePriceCalculations,
  useFormState,
} from "Store/priceCalculation/priceCalculationHooks";
import { AKA_CHILD_PRICE_STEP, AkaChildPriceFormValues } from "..";

import { AkaRenewalInfoAlert } from "Components/AkaRenewalInfoAlert";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { DittoCoverageCard } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/DittoCoverages/DittoCoverageCard";
import { StyledComparisonWrap } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/productPriceTile.styles";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { getCollectionOfDittoComponents } from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { AkaChildCoverage } from "Store/interfaces/products/coverage";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products/index";
import { ReduxState } from "Store/rootReducer";
import { Form } from "formik";
import React from "react";
import { useSelector } from "react-redux";

export const AkaChildProductPrice = (): React.ReactElement => {
  const handleAddProductToCart = useAddProductToCartHandler(
    AKA_CHILD_PRICE_STEP.id,
  );

  const initialFormState = useFormState(AKA_CHILD_PRICE_STEP.id, {
    coverage: AkaChildCoverage.AKA_CHILD_STANDARD,
  });

  const { addProductToCart } = useSelector((state: ReduxState) => state);

  const { akachild: productCardTextComponents } =
    getCollectionOfDittoComponents(TextComponentNameLevel1.COVERAGE_CARD);

  const coverages = useCoveragePriceCalculations();

  const handleSubmit = (formProps: AkaChildPriceFormValues): void => {
    const productToAdd = {
      coverage: formProps.coverage,
      productType: INSURANCE_NAME_VALUE.AKA_CHILD,
    };
    handleAddProductToCart(formProps, [productToAdd]);
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.AKA_CHILD}`,
        );
      }}
    >
      <ProductPriceTile
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        header={AKA_CHILD_PRICE_STEP.title}
      >
        <Form>
          <AkaRenewalInfoAlert productName={INSURANCE_NAME_VALUE.AKA_CHILD} />
          {[...coverages].map(([coverage, priceCalculationEntry]) => (
            <DittoCoverageCard
              key={coverage}
              fieldName="coverage"
              product={ProductBackendName.AKA_CHILD}
              coverage={coverage}
              textComponents={productCardTextComponents}
              prices={priceCalculationEntry.prices}
            />
          ))}

          {addProductToCart.error && (
            <SystemErrorAlert alertId="addProductToCart" marginTop="32px" />
          )}
          <StyledComparisonWrap>
            <NavigationButtons isLoading={addProductToCart.isLoading} />
          </StyledComparisonWrap>
          <InputFailureTracker
            productName={INSURANCE_NAME_VALUE.AKA_CHILD}
            stepId={AKA_CHILD_PRICE_STEP.id}
          />
        </Form>
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
